/**
 * @file repContractsView.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description The reps' contracts screen (Cam, 28 Oct 2026 — O4, proposal
 *              decision D12 "Grouped by customer"), pure: its filters and
 *              what they send, Clear filters, and the words a contract row
 *              shows. The server (services/repContractsService) decides whose
 *              contracts, the figures and the usage words; this only lays
 *              them out.
 *
 *   Cam's note on D12: "Standard view is filtered to the current period, and
 *   we don't need the bar if the contract does not have a tonnage on it (most
 *   don't) - for those just show the order count and total tonnage (be clear
 *   on the unit here …)". So the standard view = the server's default_period,
 *   live and on hold; a bar only where a quantity was agreed.
 */

import { formatPrice } from './currencyFormatter';
import { formatDateDDMMYY } from './dateFormatters';

/** Live and on hold, as the contract screens show them; archived on request. */
export const DEFAULT_STATES = ['live', 'on_hold'];

/** The order the type chips run in (the server's category keys). */
export const CATEGORY_ORDER = ['blends', 'compounds', 'straights', 'minerals', 'bags', 'spot', 'other', 'milk_powder'];

/**
 * The standard view. `period` null means "not chosen yet" — the screen puts
 * the current period in as soon as it knows it; '' is "Any period", chosen.
 */
export function defaultFilters(defaultPeriod = null) {
  return {
    q: '',
    category: [],
    fulfilment: [],
    state: [...DEFAULT_STATES],
    accountId: '',
    period: defaultPeriod
  };
}

const sameSet = (a = [], b = []) => a.length === b.length && a.every(x => b.includes(x));

/** Does the view differ from the standard one (what Clear filters goes back to)? */
export function filtersApplied(filters, defaultPeriod) {
  const d = defaultFilters(defaultPeriod ?? '');
  const f = filters || {};
  return Boolean(String(f.q || '').trim())
    || !sameSet(f.category || [], d.category)
    || !sameSet(f.fulfilment || [], d.fulfilment)
    || !sameSet(f.state || [], d.state)
    || String(f.accountId || '') !== ''
    || (f.period ?? '') !== (d.period ?? '');
}

/** Toggle one value in a chip list (returns a new array, in `order`). */
export function toggleIn(list = [], value, order = null) {
  const next = list.includes(value) ? list.filter(v => v !== value) : [...list, value];
  return order ? order.filter(k => next.includes(k)).concat(next.filter(k => !order.includes(k))) : next;
}

/** The query string GET /api/rep-contracts takes. */
export function listQuery(filters, { repId = '', page = 1, limit = null } = {}) {
  const f = filters || {};
  const p = new URLSearchParams();
  if (f.category && f.category.length) p.set('category', f.category.join(','));
  p.set('state', (f.state && f.state.length ? f.state : DEFAULT_STATES).join(','));
  if (f.fulfilment && f.fulfilment.length) p.set('fulfilment', f.fulfilment.join(','));
  if (f.accountId) p.set('account_id', String(f.accountId));
  if (f.period) p.set('period', f.period);
  if (String(f.q || '').trim()) p.set('q', String(f.q).trim());
  if (repId !== '' && repId !== null && repId !== undefined) p.set('rep_id', String(repId));
  p.set('page', String(page || 1));
  if (limit) p.set('limit', String(limit));
  // The orders under a contract come when it is opened (GET /contract?key=).
  p.set('orders', 'none');
  return p.toString();
}

/** "£341.00 /T", "£12.50 /25kg bag", "£341.00" when the unit is not known. */
export function priceText(value, unit) {
  if (value === null || value === undefined || value === '') return '';
  return unit ? `${formatPrice(value)} /${unit}` : formatPrice(value);
}

/** One figure or its range: "£281.50–£295.00 /T". '' when neither. */
export function priceOrRange(value, range, unit) {
  if (value !== null && value !== undefined) return priceText(value, unit);
  if (!range) return '';
  const r = `${formatPrice(range.min)}–${formatPrice(range.max)}`;
  return unit ? `${r} /${unit}` : r;
}

/**
 * The margin cell: { amount, percent, tone } — £ per unit and % on cost
 * (contractUsage.marginOf). A range for a group whose products differ.
 * No price → "No price" in the error colour; no cost → nothing.
 */
export function marginView(c) {
  if (!c) return null;
  const noPrice = (c.flags || []).some(f => f.key === 'no_price') && !(c.price > 0) && !c.price_range;
  if (noPrice) return { amount: 'No price', percent: '', tone: 'error' };
  if (c.margin) {
    return {
      amount: formatPrice(c.margin.amount),
      percent: `${c.margin.percent.toFixed(1)}% on cost`,
      tone: c.margin.amount < 0 ? 'error' : 'success'
    };
  }
  if (c.margin_range) {
    const a = c.margin_range.amount;
    const p = c.margin_range.percent;
    return {
      amount: `${formatPrice(a.min)}–${formatPrice(a.max)}`,
      percent: `${p.min.toFixed(1)}–${p.max.toFixed(1)}% on cost`,
      tone: a.min < 0 ? 'error' : 'success'
    };
  }
  return null;
}

export const STATE_LABELS = { live: 'Live', on_hold: 'On hold', archived: 'Archived' };

/** "01/10/26 – 30/04/27"; one end open reads "from" / "until". */
export function datesText(start, end) {
  if (start && end) return `${formatDateDDMMYY(start)} – ${formatDateDDMMYY(end)}`;
  if (start) return `From ${formatDateDDMMYY(start)}`;
  if (end) return `Until ${formatDateDDMMYY(end)}`;
  return '';
}

/** Ended / not started yet, in words ('' when in force today). */
export function whenText(c) {
  if (!c) return '';
  if (c.in_force === 'past') return `Ended ${formatDateDDMMYY(c.end_date)}`;
  if (c.in_force === 'future') return `Starts ${formatDateDDMMYY(c.start_date)}`;
  return '';
}

/** Has a quantity been agreed? Only then a bar (Cam's D12 note). */
export function hasAgreedQuantity(c) {
  return Boolean(c && c.kind !== 'milk_powder' && Number(c.quantity) > 0);
}

/**
 * The usage words beside a contract. With an agreed quantity the bar carries
 * "54.00 of 220.00 T · 25%" (fulfilment.text) and this says how many orders;
 * without, the server's usage text says both ("3 orders · 27.00 T ordered").
 */
export function usageLines(c) {
  const u = (c && c.usage) || null;
  if (!c) return { main: '', sub: '' };
  if (c.kind === 'milk_powder') {
    return { main: 'Prices only — no quantity', sub: u ? u.text : '' };
  }
  if (hasAgreedQuantity(c)) {
    const fulfil = c.fulfilment ? c.fulfilment.text : '';
    if (!u) return { main: fulfil, sub: '' };
    const parts = [];
    if (u.order_lines) parts.push(`${u.orders} order${u.orders === 1 ? '' : 's'}`);
    if (u.po_lines) parts.push(`${u.po_lines} PO line${u.po_lines === 1 ? '' : 's'}`);
    return { main: fulfil, sub: parts.length ? parts.join(' + ') : 'No orders yet' };
  }
  return { main: u ? u.text : (c.fulfilment ? c.fulfilment.text : ''), sub: 'No quantity agreed' };
}

/** The bar's width (0–100) and its tone: over the agreed amount is a warning. */
export function barOf(c) {
  if (!hasAgreedQuantity(c)) return null;
  const pct = c.fulfilment && c.fulfilment.percent !== null && c.fulfilment.percent !== undefined
    ? Number(c.fulfilment.percent) : 0;
  const bucket = c.fulfilment ? c.fulfilment.bucket : null;
  return {
    width: Math.max(0, Math.min(100, pct)),
    tone: bucket === 'over' ? 'warning' : bucket === 'complete' ? 'success' : 'primary'
  };
}

/** The line under a contract's title: codes, suppliers, numbers, type. */
export function contractMeta(c) {
  if (!c) return '';
  const parts = [];
  const products = c.products || [];
  if (products.length === 1) parts.push(products[0].code);
  else if (products.length > 1) parts.push(`${products.length} products`);
  const suppliers = (c.suppliers || []).map(s => s.name).filter(Boolean);
  if (suppliers.length === 1) parts.push(suppliers[0]);
  else if (suppliers.length > 1) parts.push(`${suppliers.length} suppliers`);
  const nums = [...(c.supplier_contract_numbers || []), ...(c.contract_numbers || [])].filter(Boolean);
  if (nums.length) parts.push(nums.slice(0, 2).join(', ') + (nums.length > 2 ? ` +${nums.length - 2}` : ''));
  if (c.category && c.category.label) parts.push(c.category.label);
  return parts.filter(Boolean).join(' · ');
}

/** Is it more than one product (a group of mills, a product group) — opens into them. */
export function isMultiProduct(c) {
  return Boolean(c && c.kind !== 'straights' && c.kind !== 'milk_powder' && (c.products || []).length > 1);
}

/** A customer's heading line: "3 contracts · 120.00 T agreed · 38.00 T ordered". */
export function customerSummaryText(cu, { formatTonnes }) {
  const s = (cu && cu.summary) || {};
  const parts = [`${s.contracts || 0} contract${s.contracts === 1 ? '' : 's'}`];
  if (s.agreed_tonnes > 0) parts.push(`${formatTonnes(s.agreed_tonnes)} agreed`);
  if (s.ordered_tonnes > 0) parts.push(`${formatTonnes(s.ordered_tonnes)} ordered`);
  return parts.join(' · ');
}

/** Short words for the phone's summary of what the filters are showing. */
export function activeFilterWords(filters, { options = null } = {}) {
  const f = filters || {};
  const out = [];
  out.push(f.period ? f.period : 'Any period');
  const cats = (options && options.categories) || [];
  if (f.category && f.category.length) {
    out.push(f.category.map(k => (cats.find(c => c.key === k) || {}).label || k).join(', '));
  }
  if (!sameSet(f.state || [], DEFAULT_STATES)) {
    out.push((f.state || []).map(s => STATE_LABELS[s] || s).join(', ') || 'Live, On hold');
  }
  const ful = (options && options.fulfilment) || [];
  if (f.fulfilment && f.fulfilment.length) {
    out.push(f.fulfilment.map(k => (ful.find(b => b.key === k) || {}).label || k).join(', '));
  }
  if (f.accountId) {
    const cu = ((options && options.customers) || []).find(c => String(c.account_id) === String(f.accountId));
    out.push(cu ? cu.name : 'One customer');
  }
  if (String(f.q || '').trim()) out.push(`“${String(f.q).trim()}”`);
  return out;
}

/** How many filters are set beyond the standard view (the Filters button's count). */
export function filterCount(filters, defaultPeriod) {
  const f = filters || {};
  let n = 0;
  if (f.category && f.category.length) n += 1;
  if (f.fulfilment && f.fulfilment.length) n += 1;
  if (!sameSet(f.state || [], DEFAULT_STATES)) n += 1;
  if (f.accountId) n += 1;
  if ((f.period ?? '') !== (defaultPeriod ?? '')) n += 1;
  return n;
}
